import { useState } from "react";
import { MessageCircle, PhoneCall } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { DEFAULT_RATES, inr, waLink, RATES_COMING_SOON, RATES_SOON_LABEL } from "@/lib/site";
import { useFeedback } from "@/components/Feedback";

export const SavingsScheme = ({ rates }) => {
  const [monthly, setMonthly] = useState(10000);
  const { openEnquiry } = useFeedback();
  const r22 = (rates || DEFAULT_RATES).gold_22k;

  const paid = monthly * 11;
  const maturity = paid + monthly;
  const grams = maturity / r22;
  const fill = ((monthly - 2000) / (50000 - 2000)) * 100;

  const summary = RATES_COMING_SOON
    ? `Namaste! I'd like to enrol in the Swarna Nidhi 11+1 plan at ${inr(monthly)}/month. Maturity: ${inr(maturity)}.`
    : `Namaste! I'd like to enrol in the Swarna Nidhi 11+1 plan at ${inr(monthly)}/month. Maturity: ${inr(maturity)} (≈${grams.toFixed(2)}g gold).`;

  return (
    <section id="scheme" data-testid="savings-scheme-section" className="relative mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <SectionHead
            eyebrow="Swarna Nidhi · 11+1"
            title={<>Pay eleven months. <span className="gold-text italic">We gift the twelfth.</span></>}
            sub="Aradhana's monthly gold accumulation plan — build your bridal or festive gold slowly, and let the house honour your discipline."
          />
          <Reveal delay={0.1}>
            <ul className="space-y-5">
              {[
                ["The 12th month, on us", "Pay 11 instalments — Aradhana credits the 12th month bonus instalment."],
                ["50% off making charges", "Scheme members enjoy half making charges on redemption jewellery."],
                ["Golden welcome coin", "A complimentary 24K lakshmi coin when your plan matures."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-5">
                  <span className="font-mono text-xs text-[#C59B27]">0{i + 1}</span>
                  <div>
                    <p className="font-display text-sm font-medium text-[#F3E5AB]">{t}</p>
                    <p className="mt-1 text-sm font-light text-[#B5B0A8]">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="glass-card rounded-3xl p-8 lg:p-10">
            <div className="flex items-end justify-between">
              <p className="eyebrow">Monthly instalment</p>
              <p data-testid="savings-plan-monthly-value" className="font-mono text-4xl text-[#E6C265]">{inr(monthly)}</p>
            </div>
            <input
              data-testid="savings-plan-slider"
              type="range"
              min="2000"
              max="50000"
              step="500"
              value={monthly}
              style={{ "--fill": `${fill}%` }}
              onChange={(e) => setMonthly(Number(e.target.value))}
              className="mt-8 w-full"
            />
            <div className="mt-2 flex justify-between font-mono text-[10px] text-[#79756E]">
              <span>₹2,000</span>
              <span>₹50,000</span>
            </div>
            <div className="mt-10 space-y-4 border-t border-white/5 pt-8 font-mono text-sm">
              <div className="flex justify-between text-[#B5B0A8]">
                <span>Your 11 instalments</span>
                <span data-testid="savings-plan-paid-value" className="text-white">{inr(paid)}</span>
              </div>
              <div className="flex justify-between text-[#B5B0A8]">
                <span>12th month · Aradhana's gift</span>
                <span data-testid="savings-plan-bonus-value" className="text-[#E6C265]">+ {inr(monthly)}</span>
              </div>
              <div className="flex justify-between border-t border-[#D4AF37]/20 pt-4 text-[#B5B0A8]">
                <span>Maturity value</span>
                <span data-testid="savings-plan-maturity-value" className="text-2xl text-[#E6C265]">{inr(maturity)}</span>
              </div>
              <div className="flex justify-between text-[#B5B0A8]">
                <span>≈ Gold weight at today's 22K</span>
                <span data-testid="savings-plan-grams-value" className="text-white">
                  {RATES_COMING_SOON ? RATES_SOON_LABEL : `${grams.toFixed(2)} g`}
                </span>
              </div>
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="savings-plan-enroll-button"
                onClick={() => openEnquiry(`Swarna Nidhi Enrolment — ${inr(monthly)}/month`, "savings-scheme")}
                className="btn-gold flex-1 !py-3"
              >
                <PhoneCall size={15} /> Request Callback
              </button>
              <a
                data-testid="savings-plan-whatsapp-button"
                href={waLink(summary)}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost flex-1 !py-3"
              >
                <MessageCircle size={15} /> Enrol on WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
